import { useMemo, useEffect, useState } from 'react'

import {
  Button,
  Dialog,
  Icon,
  useToast,
  Text,
  InputDropdown,
} from '@vallourec-ui/vallourec-ui'

import { useTranslation } from 'react-i18next'

import { CardRow, ComponentsColumn, ComponentsColumnScrollable, HeaderContainer, Row, StyledButton, StyledText } from './style'

import { RemarkReport } from '@/@types/global'
import TextFieldInput from '@/components/TextFieldInput'

interface EditModalProps {
  isOpen: boolean
  title: string
  data: RemarkReport
  setIsOpen: (isOpen: boolean) => void
  onOutsideClick: () => void
  onConfirm: (payload: RemarkReport) => void
}

const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  data,
  title,
  onOutsideClick,
  onConfirm,
  setIsOpen
}) => {
  const { toast } = useToast()
  const { t } = useTranslation()

  const [remark, setRemark] = useState<string>('')
  const [line, setLine] = useState<string>('')
  const [remarks, setRemarks] = useState<RemarkReport['remarks']>([]);

  const lineOptions = useMemo(
    () => [
      {
        key: 1,
        value: '1',
        label: t('remark.modal.options.line.1', 'Line 1'),
      },
      {
        key: 2,
        value: '2',
        label: t('remark.modal.options.line.2', 'Line 2'),
      },
      {
        key: 3,
        value: '3',
        label: t('remark.modal.options.3', 'Line 3'),
      },
    ],
    [t]
  )

  const clearModal = () => {
    setRemark('')
    setLine('')
  }

  useEffect(() => {
    if (isOpen && data) {
      setRemarks(data.remarks || [])
      clearModal()
    }
  }, [isOpen, data, lineOptions])

  const handleAddRemark = () => {
    const newRemark = { remark, line }
    setRemarks(prevRemarks => {
      const updatedRemarks = Array.isArray(prevRemarks) ? [...prevRemarks, newRemark] : [newRemark];
      return updatedRemarks;
    });
    clearModal();
  }

  const handleRemoveRemark = (index: number) => {
    setRemarks(prevRemarks => {
      const updatedRemarks = [...prevRemarks];
      updatedRemarks.splice(index, 1);
      return updatedRemarks;
    });
  };

  useEffect(() => {
    data.remarks = remarks;
  }, [remarks]);

  const handleOnSave = async () => {
    if (!remark?.trim()) {
      onConfirm(data)
      clearModal()
      onOutsideClick()
    }
    else {
      toast({
        type: 'warn',
        title: t('warning'),
        description: t(
          'remark.modal.saveValidation',
        ),
      })
      return
    }
  }

  return (
    <Dialog.Root
      isOpen={isOpen}
      setIsOpen={() => setIsOpen(false)}
      closeOnInteractOutside={false}
      width="60rem"
      header={<Dialog.Header title={title} />}
      footer={
        <Dialog.Footer
          buttonText={t('save')}
          onConfirm={handleOnSave}
          onClose={() => {
            onOutsideClick()
            clearModal()
          }}
        />
      }
    >
      <HeaderContainer>
        <Row>
          <ComponentsColumn>
            <StyledText>{t<string>('remark.modal.remark', 'Remark')}</StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="userFirstName"
              disabled={false}
              onChange={(event) => setRemark(event.target.value)}
              placeholder={t('remark.modal.enterRemark', 'Enter a remark')}
              width="100%"
              value={remark}
            />
          </ComponentsColumn>
          <ComponentsColumn>
            <StyledText>{t<string>('remark.modal.line', 'Line')}</StyledText>
            <InputDropdown.Root
              onValueChange={(e) => setLine(e)}
              trigger={<InputDropdown.Trigger placeholder={t('remark.modal.selectLine', "Select a line")} />}
            >
                    {lineOptions.map((item) => {
                return (
                  <InputDropdown.Item
                    key={`SelectItem-${item.value}`}
                    value={item.value?.toString()}
                  >
                    {item.label}
                  </InputDropdown.Item>
                )
              })}
          </InputDropdown.Root>
          </ComponentsColumn>
          <Button size="sm" variant="primary" onClick={handleAddRemark} disabled={!remark.trim() || !line}>
            <Icon
              name="AddLine"
              color="neutral-color-0"
              width={24}
              height={24}
            />
          </Button>
        </Row>
        <StyledText>{t<string>('remark.modal.currentRemarks', 'Current Remarks')}</StyledText>
        <ComponentsColumnScrollable>
          {Array.isArray(remarks) &&
            remarks.map((item: { remark: string; line: string }, idx) => (
                <CardRow>
                  <ComponentsColumn style={{ width: '95%' }}>
                    <Text size="small">
                      {t('remark.modal.line')} {item.line} - {item.remark}
                    </Text>
                  </ComponentsColumn>
                  <ComponentsColumn style={{ width: '5%' }}>
                    <StyledButton
                      variant="errorWithBorders"
                      size="sm"
                      onClick={() =>
                        handleRemoveRemark(idx)
                      }
                    >
                      <Icon
                        name="DeleteBin6Line"
                        color="neutral-color-0"
                        width={18}
                        height={18}
                      />
                    </StyledButton>
                  </ComponentsColumn>
                </CardRow>
            
            ))}
        </ComponentsColumnScrollable>
      </HeaderContainer>
    </Dialog.Root>
  )
}

export default EditModal
